import React from 'react';
import MediaQuery from 'react-responsive';

import { SkinAnalysisSectionIdentifier } from '../../../Types';
import { DataProcessingScope } from '../../../Api/Types';

import { AccountContext } from '../../../Context/AccountContext';

import { getSectionIDs, getSectionLabel } from '../../../MyLom/SkinAnalyses/Labels';

import Button from '../../../Form/Button/Button';
import SkinAnalysesDataProcessingConsent from '../../../MyLom/SkinAnalyses/SkinAnalysesDataProcessingConsent';
import SkinAnalysisLinks from '../../../SkinAnalysisTable/Components/SkinAnalysisLinks';
import SkinAnalysisSection from '../../../SkinAnalysisTable/SkinAnalysisSection';

import './PersonalFilter.scss';

interface Props {
    toggleVisibility: () => void;
    isVisible: boolean;
}

const PersonalFilter: React.FunctionComponent<Props> = (props) => {
    const { accountData } = React.useContext(AccountContext);

    const apply = (event: React.FormEvent) => {
        props.toggleVisibility();
    };

    if (!accountData) {
        throw new Error('Personal filter component requires account data');
    }

    if (!props.isVisible) {
        return null;
    }

    if (accountData.dataProcessingConsents.indexOf(DataProcessingScope.SkinAnalyses) === -1) {
        return (
            <div className="personal-filter skin-analysis">
                <SkinAnalysesDataProcessingConsent />
            </div>
        );
    }

    if (
        accountData.skinAnalyses.length === 0 &&
        accountData.profiles.every((profile) => profile.skinAnalyses.length === 0) &&
        accountData.onlineAnalyses.length === 0
    ) {
        return (
            <div className="personal-filter skin-analysis">
                <p>Es wurde noch keine Hautbild-Analyse durchgeführt.</p>
                <SkinAnalysisLinks toggleVisibility={props.toggleVisibility} />
            </div>
        );
    }

    const sectionIDs = getSectionIDs();

    return (
        <>
            <div className="personal-filter skin-analysis">
                {sectionIDs.map((skinAnalysisSectionIdentifier: SkinAnalysisSectionIdentifier) => {
                    // eslint-disable-next-line rulesdir/use-to-sorted-over-sort
                    const skinAnalysesInSection = [
                        ...accountData.skinAnalyses.filter((analysis) => {
                            return analysis.answeredSections.includes(skinAnalysisSectionIdentifier);
                        }),
                        ...(skinAnalysisSectionIdentifier === 'Face' ? accountData.onlineAnalyses : []),
                    ].sort((a, b) => (a.creationDateTime < b.creationDateTime ? 1 : -1));
                    return (
                        <SkinAnalysisSection
                            key={skinAnalysisSectionIdentifier}
                            skinAnalysisSectionIdentifier={skinAnalysisSectionIdentifier}
                            skinAnalyses={skinAnalysesInSection}
                            showLinks={true}
                            useReactRouterLink={false}
                            toggleVisibility={props.toggleVisibility}
                            label={
                                getSectionLabel(skinAnalysisSectionIdentifier) +
                                ` - ${accountData.firstName} ${accountData.lastName}`
                            }
                        />
                    );
                })}
                {accountData?.profiles.map((profile) => {
                    return (
                        <React.Fragment key={profile.id}>
                            {sectionIDs.map((skinAnalysisSectionIdentifier: SkinAnalysisSectionIdentifier) => {
                                const profileSkinAnalyses = profile.skinAnalyses || [];
                                if (profileSkinAnalyses.length === 0) {
                                    return null;
                                }
                                const skinAnalysesInSection = profileSkinAnalyses
                                    .filter((analysis) => {
                                        return analysis.answeredSections.includes(skinAnalysisSectionIdentifier);
                                    })
                                    .sort((a, b) => (a.creationDateTime < b.creationDateTime ? 1 : -1));
                                return (
                                    <SkinAnalysisSection
                                        key={skinAnalysisSectionIdentifier + '_profile'}
                                        skinAnalysisSectionIdentifier={skinAnalysisSectionIdentifier}
                                        skinAnalyses={skinAnalysesInSection}
                                        showLinks={true}
                                        useReactRouterLink={false}
                                        toggleVisibility={props.toggleVisibility}
                                        label={
                                            getSectionLabel(skinAnalysisSectionIdentifier) + ' - ' + profile.profileName
                                        }
                                    />
                                );
                            })}
                        </React.Fragment>
                    );
                })}
                <MediaQuery minWidth={915}>
                    <div className="d-flex button-wrapper">
                        <Button primary={true} type="button" className="personal-filter__button" onClick={apply}>
                            Filter anwenden
                        </Button>
                    </div>
                </MediaQuery>
            </div>
        </>
    );
};

export default PersonalFilter;
